import { useRef, useEffect } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Undo, Redo } from "lucide-react";

/**
 * Lightweight dependency-free rich text editor (contentEditable + toolbar).
 * Emits HTML via onChange. Premium dark styling to match the admin.
 */
export function RichTextEditor({ value, onChange, testId = "rte" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };

  const emit = () => onChange?.(ref.current?.innerHTML || "");

  const addLink = () => {
    const url = window.prompt("Link URL");
    if (url) exec("createLink", url);
  };

  const Btn = ({ icon: Icon, cmd, arg, action, label }) => (
    <button
      type="button"
      title={label}
      onMouseDown={(e) => {
        e.preventDefault();
        action ? action() : exec(cmd, arg);
      }}
      className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
    >
      <Icon size={16} />
    </button>
  );

  return (
    <div className="border border-white/20 bg-black" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-white/12 p-2">
        <Btn icon={Bold} cmd="bold" label="Bold" />
        <Btn icon={Italic} cmd="italic" label="Italic" />
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn icon={Heading2} cmd="formatBlock" arg="<h2>" label="Heading 2" />
        <Btn icon={Heading3} cmd="formatBlock" arg="<h3>" label="Heading 3" />
        <Btn icon={Quote} cmd="formatBlock" arg="<blockquote>" label="Quote" />
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn icon={List} cmd="insertUnorderedList" label="Bullet list" />
        <Btn icon={ListOrdered} cmd="insertOrderedList" label="Numbered list" />
        <Btn icon={Link2} action={addLink} label="Link" />
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn icon={Undo} cmd="undo" label="Undo" />
        <Btn icon={Redo} cmd="redo" label="Redo" />
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onBlur={emit}
        data-testid={`${testId}-body`}
        className="rte-content min-h-[320px] max-h-[520px] overflow-y-auto p-5 text-white/90 font-body leading-relaxed focus:outline-none"
        suppressContentEditableWarning
      />
    </div>
  );
}

export default RichTextEditor;
